import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

import { PerformanceSliceState, ColumnVisibility } from './performance.type';
import {
    PERFORMANCE_TABLE_COLUMN_FLOW,
    PERFORMANCE_TABLE_COLUMN_TYPE,
    PERFORMANCE_TABLE_COLUMN_STATUS,
    PERFORMANCE_TABLE_COLUMN_PUBLISHED_AT,
    PERFORMANCE_TABLE_COLUMN_IMPRESSIONS,
    PERFORMANCE_TABLE_COLUMN_STARTED,
    PERFORMANCE_TABLE_COLUMN_START_RATE,
    PERFORMANCE_TABLE_COLUMN_BUTTON_CLICKS,
    PERFORMANCE_TABLE_COLUMN_BUTTON_ENGAGEMENT,
    PERFORMANCE_TABLE_COLUMN_TOTAL_WATCH_TIME_SECONDS,
    PERFORMANCE_TABLE_COLUMN_ACTIVATION_URL,
    PERFORMANCE_TABLE_COLUMN_OBJECTIVE,
    PERFORMANCE_TABLE_COLUMN_VIDEO_REELS_PLAYED,
    ColumnId,
} from '@/Pages/Performance/Performance.type';

export const getDefaultColumnVisibility = (): ColumnVisibility => {
    return {
        [PERFORMANCE_TABLE_COLUMN_FLOW]: true,
        [PERFORMANCE_TABLE_COLUMN_TYPE]: true,
        [PERFORMANCE_TABLE_COLUMN_STATUS]: true,
        [PERFORMANCE_TABLE_COLUMN_PUBLISHED_AT]: true,
        [PERFORMANCE_TABLE_COLUMN_IMPRESSIONS]: true,
        [PERFORMANCE_TABLE_COLUMN_STARTED]: true,
        [PERFORMANCE_TABLE_COLUMN_START_RATE]: true,
        [PERFORMANCE_TABLE_COLUMN_BUTTON_CLICKS]: true,
        [PERFORMANCE_TABLE_COLUMN_BUTTON_ENGAGEMENT]: true,
        [PERFORMANCE_TABLE_COLUMN_TOTAL_WATCH_TIME_SECONDS]: true,
        [PERFORMANCE_TABLE_COLUMN_ACTIVATION_URL]: false,
        [PERFORMANCE_TABLE_COLUMN_OBJECTIVE]: false,
        [PERFORMANCE_TABLE_COLUMN_VIDEO_REELS_PLAYED]: false,
    };
};

const performanceInitialState: PerformanceSliceState = {
    tableFilterMap: {},
    showMetricDefinitions: true,
    columnVisibility: getDefaultColumnVisibility(),
};

export const PERFORMANCE_REDUCER_NAME = 'performance';

export const performanceSlice = createSlice({
    name: PERFORMANCE_REDUCER_NAME,
    initialState: performanceInitialState,
    reducers: {
        setTableFilter: (
            state,
            action: PayloadAction<{
                filterId: keyof PerformanceSliceState['tableFilterMap'];
                value: string | null;
            }>,
        ) => {
            state.tableFilterMap[action.payload.filterId] =
                action.payload.value;
        },
        resetTableFilterMap: (state) => {
            state.tableFilterMap = {};
        },
        updateShowMetricDefinitions: (
            state,
            action: PayloadAction<boolean>,
        ) => {
            state.showMetricDefinitions = action.payload;
        },
        setColumnsVisibility: (
            state,
            action: PayloadAction<Record<ColumnId, boolean>>,
        ) => {
            Object.entries(action.payload).forEach(([columnId, visible]) => {
                state.columnVisibility[columnId] = visible;
            });
        },
        resetColumnVisibility: (state) => {
            state.columnVisibility = getDefaultColumnVisibility();
        },
    },
});

export const {
    setTableFilter,
    resetTableFilterMap,
    updateShowMetricDefinitions,
    setColumnsVisibility,
    resetColumnVisibility,
} = performanceSlice.actions;

export const performanceReducer = performanceSlice.reducer;
